/**
 * Token Price Service Configuration
 * Defines tokens, chains, and LP sources for on-chain price lookups
 */

import { BASE_CHAIN, BNB_CHAIN } from './chains'

// Chain configurations for price lookups
export interface ChainPriceConfig {
  chainId: number
  name: string
  rpcUrls: string[]
  nativeToken: {
    symbol: string
    decimals: number
    // Address of wrapped native token (WETH, WBNB, etc.)
    wrappedAddress: string
  }
  // Common stablecoin addresses for USD conversion
  stablecoins: {
    symbol: string
    address: string
    decimals: number
  }[]
}

// LP Pool configuration
export interface LPPoolConfig {
  address: string
  dex: 'aerodrome' | 'aerodrome-cl' | 'pancakeswap-v3' | 'pancakeswap-v2' | 'uniswap-v2' | 'uniswap-v3'
  // Token pair info
  token0: string
  token1: string
  // Fee tier for v3 pools (in basis points, e.g., 3000 = 0.3%)
  feeTier?: number
}

// Token configuration
export interface TokenPriceConfig {
  symbol: string
  decimals: number
  chains: {
    chainId: number
    address: string
    // LP pools to use for price discovery
    pools: LPPoolConfig[]
  }[]
}

// ============================================================================
// Chain Configurations
// ============================================================================

export const BASE_PRICE_CONFIG: ChainPriceConfig = {
  chainId: BASE_CHAIN.id,
  name: BASE_CHAIN.name,
  rpcUrls: BASE_CHAIN.rpcUrls,
  nativeToken: {
    symbol: 'ETH',
    decimals: 18,
    wrappedAddress: '0x4200000000000000000000000000000000000006', // WETH on Base
  },
  stablecoins: [
    {
      symbol: 'USDC',
      address: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
      decimals: 6,
    },
    {
      symbol: 'USDbC',
      address: '0xd9aAEc86B65D86f6A7B5B1b0c42FFA531710b6CA',
      decimals: 6,
    },
  ],
}

export const BNB_PRICE_CONFIG: ChainPriceConfig = {
  chainId: BNB_CHAIN.id,
  name: BNB_CHAIN.name,
  rpcUrls: BNB_CHAIN.rpcUrls,
  nativeToken: {
    symbol: 'BNB',
    decimals: 18,
    wrappedAddress: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // WBNB
  },
  stablecoins: [
    {
      symbol: 'USDT',
      address: '0x55d398326f99059fF775485246999027B3197955',
      decimals: 18,
    },
    {
      symbol: 'BUSD',
      address: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56',
      decimals: 18,
    },
    {
      symbol: 'USDC',
      address: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
      decimals: 18,
    },
  ],
}

// Hardhat local development chain
export const HARDHAT_PRICE_CONFIG: ChainPriceConfig = {
  chainId: 1337,
  name: 'Hardhat Local',
  rpcUrls: ['http://127.0.0.1:8545'],
  nativeToken: {
    symbol: 'BNB',
    decimals: 18,
    wrappedAddress: '0x0000000000000000000000000000000000000000', // No WBNB on local
  },
  stablecoins: [], // No stablecoins on local
}

export const CHAIN_PRICE_CONFIGS: Record<number, ChainPriceConfig> = {
  [BASE_PRICE_CONFIG.chainId]: BASE_PRICE_CONFIG,
  [BNB_PRICE_CONFIG.chainId]: BNB_PRICE_CONFIG,
  [HARDHAT_PRICE_CONFIG.chainId]: HARDHAT_PRICE_CONFIG,
}

// ============================================================================
// Token Configurations
// ============================================================================

export const WELEPHANT_PRICE_CONFIG: TokenPriceConfig = {
  symbol: 'WELEPHANT',
  decimals: 9,
  chains: [
    {
      chainId: 8453, // Base
      address: '0x7d03acf64a1a64bff9d24850011cca93c78a22e7',
      pools: [
        {
          // WELEPHANT/WETH pool on Aerodrome CL (concentrated liquidity)
          address: '0x89845726448aB9E1a08629D55Aba85de3f377c0E',
          dex: 'aerodrome-cl',
          token0: '0x7d03acf64a1a64bff9d24850011cca93c78a22e7', // WELEPHANT
          token1: '0x4200000000000000000000000000000000000006', // WETH
        },
      ],
    },
    {
      chainId: 56, // BNB Chain
      address: '0x241F21dF529C05289A00dAfeCEa10139A287cDCa',
      pools: [
        {
          // WELEPHANT/WBNB pool on PancakeSwap v3
          address: '0xE1Ed6A52F6BFeCcf14ee8BD55E04FaDC78Ea6E78',
          dex: 'pancakeswap-v3',
          token0: '0x241F21dF529C05289A00dAfeCEa10139A287cDCa', // WELEPHANT
          token1: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // WBNB
          feeTier: 10000, // 1% fee tier
        },
      ],
    },
  ],
}

export const ELEPHANT_PRICE_CONFIG: TokenPriceConfig = {
  symbol: 'ELEPHANT',
  decimals: 9,
  chains: [
    {
      chainId: 56, // BNB Chain
      address: '0xE283D0e3B8c102BAdF5E8166B73E02D96d92F688',
      pools: [
        {
          // ELEPHANT/WBNB pool on PancakeSwap V2
          address: '0x1CEa83EC5E48D9157fCAe27a19807BeF79195Ce1',
          dex: 'pancakeswap-v2',
          token0: '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c', // WBNB (sorted first)
          token1: '0xE283D0e3B8c102BAdF5E8166B73E02D96d92F688', // ELEPHANT
        },
        {
          // ELEPHANT/BUSD pool on PancakeSwap V2
          address: '0x647bc907d520C3f63bE38d01DBd979f5606beC48',
          dex: 'pancakeswap-v2',
          token0: '0xE283D0e3B8c102BAdF5E8166B73E02D96d92F688', // ELEPHANT (sorted first)
          token1: '0xe9e7CEA3DedcA5984780Bafc599bD69ADd087D56', // BUSD
        },
      ],
    },
  ],
}

// All supported tokens for price lookups
export const TOKEN_PRICE_CONFIGS: Record<string, TokenPriceConfig> = {
  WELEPHANT: WELEPHANT_PRICE_CONFIG,
  ELEPHANT: ELEPHANT_PRICE_CONFIG,
}

// ============================================================================
// DEX Router/Factory Addresses (for pool discovery)
// ============================================================================

export const DEX_ADDRESSES = {
  base: {
    aerodrome: {
      factory: '0x420DD381b31aEf6683db6B902084cB0FFECe40Da',
      router: '0xcF77a3Ba9A5CA399B7c97c74d54e5b1Beb874E43',
    },
  },
  bnb: {
    pancakeswapV3: {
      factory: '0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865',
      quoter: '0xB048Bbc1Ee6b733FFfCFb9e9CeF7375518e25997',
    },
    pancakeswapV2: {
      factory: '0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73',
      router: '0x10ED43C718714eb63d5aA57B78B54704E256024E',
    },
  },
}

// ============================================================================
// Price Feed Addresses (Chainlink, etc.)
// ============================================================================

export const PRICE_FEEDS = {
  base: {
    ETH_USD: '0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70',
  },
  bnb: {
    BNB_USD: '0x0567F2323251f0Aab15c8dFb1967E4e8A7D42aeE',
  },
}
